import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useNodesState, useEdgesState, type Node, type Edge } from '@xyflow/react'
import { MindmapService } from '../../../services/mindmap/mindmapService'
import {
  createNodeData,
  createEdge,
  toBackendNodeFormat,
  toBackendEdgeFormat,
} from '../../../utils'
import { validateMindmapData } from '../../../utils/duplicateDetection'
import type { MindMapNode, MindMapEdge } from '@bandos/shared'
import type {
  ReactFlowNodeData,
  SummarizedNodeData,
  CollapsedHierarchyNode,
  StoryboardSceneBible,
  StoryboardCast,
} from '../../../types/mindmap-components'
import type { SaveQueueContextValue } from '../../../context/SaveQueueContextTypes'
import type { DataAccessMode } from '../../../context/dataAccess'

/**
 * Backend summarized node data structure
 * Used when loading collapsed/summarized nodes from the backend
 */
interface BackendSummarizedData {
  category: 'summarized'
  label?: string
  originalNodeData?: ReactFlowNodeData
  collapsedHierarchy?: CollapsedHierarchyNode
  originalNodeType?: string
  originalPosition?: { x: number; y: number }
  hiddenDescendantIds?: string[]
  hiddenEdgeIds?: string[]
}

/**
 * Backend storyboard node data structure
 */
interface BackendStoryboardData {
  category: 'storyboard'
  label?: string
  storyboardUrls?: string[]
  parentLabel?: string
  parentNodeId?: string
  frameDescriptions?: string[]
  frameCaptions?: string[]
  sceneBible?: StoryboardSceneBible
  cast?: StoryboardCast
}

const DEPRECATED_SOURCE_HANDLES: Record<string, string> = {
  'validation-source': 'right-source',
}

function normalizeSourceHandle(handle: string | null | undefined): string {
  if (!handle) return 'right-source'
  return DEPRECATED_SOURCE_HANDLES[handle] ?? handle
}

interface UseMindMapLoaderConfig {
  mindmapId: string | undefined
  antdNotification: {
    error: (config: { title: string; description: string }) => void
    warning: (config: { title: string; description: string }) => void
  }
  saveQueue: SaveQueueContextValue
  /** Determines which API endpoints to use for loading data */
  dataAccessMode: DataAccessMode
}

interface UseMindMapLoaderReturn {
  // React Flow state
  nodes: Node<ReactFlowNodeData>[]
  edges: Edge[]
  setNodes: React.Dispatch<React.SetStateAction<Node<ReactFlowNodeData>[]>>
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>
  onNodesChange: ReturnType<typeof useNodesState<Node<ReactFlowNodeData>>>[2]
  onEdgesChange: ReturnType<typeof useEdgesState<Edge>>[2]

  // Loading state
  isLoading: boolean

  // Not found state (404)
  notFound: boolean

  // Error state (non-404 errors)
  loadError: string | null

  // Dirty state
  hasUnsavedChanges: boolean
  currentStateHash: string

  // For external use (e.g., by save function)
  markAsSaved: () => void

  // Archive state (for read-only mode)
  isArchived: boolean

  // Mindmap metadata
  mindmapTitle: string
  mindmapImageStyle: string | null
  setMindmapTitle: (title: string) => void
  setMindmapImageStyle: (style: string | null) => void
}

/**
 * Hook that handles loading mindmap data from the backend and managing dirty state.
 *
 * Responsibilities:
 * - Load mindmap data on mount/mindmapId change
 * - Transform backend format to React Flow format
 * - Track dirty state (hasUnsavedChanges) via hash comparison
 * - Auto-save when changes are detected
 * - Detect and log duplicate node/edge IDs
 */
export function useMindMapLoader({
  mindmapId,
  antdNotification,
  saveQueue,
  dataAccessMode,
}: UseMindMapLoaderConfig): UseMindMapLoaderReturn {
  const isPublicAccess = dataAccessMode === 'public'
  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<ReactFlowNodeData>>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])

  // Loading state
  const [isLoading, setIsLoading] = useState(true)

  // Not found state (404)
  const [notFound, setNotFound] = useState(false)

  // Error state (non-404 errors)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Archive state (for read-only mode)
  const [isArchived, setIsArchived] = useState(false)

  // Mindmap metadata
  const [mindmapTitle, setMindmapTitle] = useState('')
  const [mindmapImageStyle, setMindmapImageStyle] = useState<string | null>(null)

  // Refs for dirty state tracking
  const savedStateHashRef = useRef<string>('')
  const hasInitialLoadCompletedRef = useRef(false)

  // Stable refs for nodes/edges that only update when content changes (not on every render)
  // This prevents downstream effects from triggering on zoom/pan when content hasn't changed
  const stableNodesRef = useRef<Node<ReactFlowNodeData>[]>([])
  const stableEdgesRef = useRef<Edge[]>([])
  const lastStableHashRef = useRef<string>('')

  // Track if we've completed the first render after data load
  // This ensures React Flow's internal store is properly synced before we start using stable refs
  const [hasInitialSyncCompleted, setHasInitialSyncCompleted] = useState(false)

  /**
   * Compute a hash of the saveable data (excluding UI-only properties and functions).
   * Used to detect actual changes vs UI-only changes.
   */
  const computeStateHash = useCallback((nodeList: typeof nodes, edgeList: typeof edges): string => {
    const saveableNodes = nodeList.map((n) => ({
      id: n.id,
      type: n.type,
      position: n.position,
      data: Object.fromEntries(
        Object.entries(n.data).filter(
          ([key, value]) =>
            typeof value !== 'function' &&
            !['isFilteredOut', 'toolbarVisible', 'autoEditNodeId'].includes(key)
        )
      ),
    }))
    const saveableEdges = edgeList.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: e.type,
    }))
    return JSON.stringify({ nodes: saveableNodes, edges: saveableEdges })
  }, [])

  // Current state hash - recomputed when nodes/edges change
  const currentStateHash = useMemo(() => {
    return computeStateHash(nodes, edges)
  }, [nodes, edges, computeStateHash])

  // Update stable refs only when content actually changes (hash changes)
  // This prevents downstream effects from triggering on zoom/pan
  if (currentStateHash !== lastStableHashRef.current && currentStateHash !== '') {
    stableNodesRef.current = nodes
    stableEdgesRef.current = edges
    lastStableHashRef.current = currentStateHash
  }

  // hasUnsavedChanges is TRUE if current state differs from saved state
  const hasUnsavedChanges =
    savedStateHashRef.current !== '' && currentStateHash !== savedStateHashRef.current

  // Mark current state as saved
  const markAsSaved = useCallback(() => {
    savedStateHashRef.current = currentStateHash
  }, [currentStateHash])

  // Stable refs so the load effect doesn't need antdNotification/setNodes/setEdges as deps
  // (they're unstable references that would cause spurious re-runs and double-loads)
  const antdNotificationRef = useRef(antdNotification)
  antdNotificationRef.current = antdNotification
  const setNodesRef = useRef(setNodes)
  setNodesRef.current = setNodes
  const setEdgesRef = useRef(setEdges)
  setEdgesRef.current = setEdges
  const cancelPendingSavesRef = useRef(saveQueue.cancelPendingSaves)
  cancelPendingSavesRef.current = saveQueue.cancelPendingSaves

  // Reset state when mindmapId changes.
  // useLayoutEffect (not useEffect) so this runs synchronously before browser paint,
  // preventing a one-frame flash of the previous project's nodes when navigating.
  useLayoutEffect(() => {
    setIsLoading(true)
    setNodes([])
    setEdges([])
    // Reset the saved hash so hasUnsavedChanges stays false while the new map loads.
    // Without this, the empty nodes/edges state compares against the previous map's hash,
    // triggers an auto-save, and can overwrite the new map with an empty mindmap.
    savedStateHashRef.current = ''
    // Cancel any debounced save from the previous map. Even if hasUnsavedChanges goes back
    // to false before the timer fires, the old save closure still holds empty nodes/edges.
    cancelPendingSavesRef.current()
    hasInitialLoadCompletedRef.current = false
    setNotFound(false)
    setLoadError(null)
    setHasInitialSyncCompleted(false)
  }, [mindmapId, setNodes, setEdges])

  // After loading completes and we have nodes, mark initial sync as complete on the NEXT render
  // This gives React Flow one render cycle to sync its internal store with the nodes prop
  useEffect(() => {
    if (!isLoading && nodes.length > 0 && !hasInitialSyncCompleted) {
      // Use setTimeout to ensure this runs after the current render cycle
      const timer = setTimeout(() => {
        setHasInitialSyncCompleted(true)
      }, 0)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [isLoading, nodes.length, hasInitialSyncCompleted])

  // Load mindmap data from backend.
  // Uses isCancelled flag to handle React StrictMode's double-invocation of effects:
  // StrictMode runs cleanup then re-runs effects, which would cause double fetches.
  // When cleanup sets isCancelled=true, the first fetch's state updates are discarded.
  useEffect(() => {
    let isCancelled = false

    const loadMindmapData = async () => {
      const setNodes = setNodesRef.current
      const setEdges = setEdgesRef.current

      if (!mindmapId) {
        if (!isCancelled) setIsLoading(false)
        return
      }

      try {
        if (!isCancelled) setIsLoading(true)

        // Use public endpoint if in public view mode, otherwise use authenticated endpoint
        const { data, error } = isPublicAccess
          ? await MindmapService.getPublic(mindmapId)
          : await MindmapService.get(mindmapId)

        // Discard result if this effect run was cancelled (StrictMode remount)
        if (isCancelled) return

        if (error) {
          if (error.status === 404) {
            setNotFound(true)
          } else {
            setLoadError(error.message || 'Failed to load mindmap')
          }
          setIsLoading(false)
          return
        }

        if (!data) {
          setNotFound(true)
          setIsLoading(false)
          return
        }

        // Check for duplicates in backend data
        validateMindmapData(data.content.nodes, data.content.edges)

        // Set archived state for read-only mode
        setIsArchived(data.is_archived === true)

        // Store metadata
        setMindmapTitle(data.title)
        setMindmapImageStyle(data.image_style ?? null)

        // Filter out requirement nodes (obsolete) and convert them to information nodes
        const filteredNodes = data.content.nodes.map((backendNode: MindMapNode) => {
          if ((backendNode.data.category as string) === 'requirement') {
            console.warn(
              `[MindMapLoader] Converting obsolete requirement node ${backendNode.id} to information node`
            )
            return {
              ...backendNode,
              data: {
                ...backendNode.data,
                category: 'information' as const,
              },
              type: 'informationNode',
            }
          }
          return backendNode
        })

        // Get hidden node/edge IDs from any summarized nodes
        const hiddenNodeIds = new Set<string>()
        const hiddenEdgeIds = new Set<string>()
        filteredNodes.forEach((backendNode: MindMapNode) => {
          // Use string comparison to avoid type narrowing issues
          if ((backendNode.data.category as string) === 'summarized') {
            const summarizedData = backendNode.data as unknown as BackendSummarizedData
            summarizedData.hiddenDescendantIds?.forEach((id: string) => hiddenNodeIds.add(id))
            summarizedData.hiddenEdgeIds?.forEach((id: string) => hiddenEdgeIds.add(id))
          }
        })

        // Transform nodes to React Flow format
        const transformedNodes = filteredNodes.map(
          (backendNode: MindMapNode): Node<ReactFlowNodeData> => {
            // Use string cast for category comparison to avoid type narrowing issues
            const categoryStr = backendNode.data.category as string

            // Handle summarized/collapsed nodes specially
            if (categoryStr === 'summarized') {
              const summarizedBackendData = backendNode.data as unknown as BackendSummarizedData
              const summarizedData: SummarizedNodeData = {
                id: backendNode.id,
                label: summarizedBackendData.originalNodeData?.label ?? '',
                category: 'summarized' as const,
                collapsedHierarchy:
                  summarizedBackendData.collapsedHierarchy as CollapsedHierarchyNode,
                originalNodeType: summarizedBackendData.originalNodeType as string,
                originalNodeData: summarizedBackendData.originalNodeData as ReactFlowNodeData,
                originalPosition: summarizedBackendData.originalPosition as {
                  x: number
                  y: number
                },
                hiddenDescendantIds: summarizedBackendData.hiddenDescendantIds ?? [],
                hiddenEdgeIds: summarizedBackendData.hiddenEdgeIds ?? [],
              }
              return {
                id: backendNode.id,
                position: backendNode.position,
                data: summarizedData as ReactFlowNodeData,
                type: 'summarizedNode',
              }
            }

            // Handle storyboard nodes specially - preserve storyboard-specific fields
            if (categoryStr === 'storyboard') {
              const storyboardBackendData = backendNode.data as unknown as BackendStoryboardData
              return {
                id: backendNode.id,
                position: backendNode.position,
                data: {
                  id: backendNode.id,
                  label: storyboardBackendData.label ?? '',
                  category: 'storyboard' as const,
                  mindmapId,
                  storyboardUrls: storyboardBackendData.storyboardUrls ?? [],
                  parentLabel: storyboardBackendData.parentLabel ?? '',
                  parentNodeId: storyboardBackendData.parentNodeId ?? '',
                  frameDescriptions: storyboardBackendData.frameDescriptions,
                  frameCaptions: storyboardBackendData.frameCaptions,
                  // Scene bible and cast for consistent regeneration
                  sceneBible: storyboardBackendData.sceneBible,
                  cast: storyboardBackendData.cast,
                  isGenerating: false,
                },
                type: 'storyboardNode',
                hidden: hiddenNodeIds.has(backendNode.id),
              }
            }

            // Check if this node should be hidden (is a descendant of a collapsed node)
            const isHidden = hiddenNodeIds.has(backendNode.id)

            const nodeData = createNodeData({
              nodeId: backendNode.id,
              category: backendNode.data.category,
              mindmapId,
              initialData: {
                ...backendNode.data,
                id: backendNode.id,
                labels: [], // Will be loaded separately
              },
            })
            return {
              id: backendNode.id,
              position: backendNode.position,
              data: nodeData,
              type: `${backendNode.data.category}Node`,
              hidden: isHidden,
            }
          }
        )

        // Transform edges to React Flow format
        const transformedEdges = data.content.edges.map((edge: MindMapEdge) => {
          const createdEdge = createEdge(
            edge.source,
            edge.target,
            transformedNodes,
            data.content.edges.map((e) => ({
              id: e.id,
              source: e.source,
              target: e.target,
              type: 'default' as const,
            })),
            {
              id: edge.id, // Preserve the backend edge ID so hiddenEdgeIds matching works on reload
              animated: edge.animated,
              sourceHandle: normalizeSourceHandle(edge.sourceHandle),
              targetHandle: edge.targetHandle ?? 'target',
            }
          )
          // Hide edges that are part of a collapsed subtree
          if (hiddenEdgeIds.has(createdEdge.id)) {
            return { ...createdEdge, hidden: true }
          }
          return createdEdge
        })

        // Check for duplicates before setting
        const transformedValidation = validateMindmapData(transformedNodes, transformedEdges)

        const nodesToSet = transformedValidation.hasDuplicates
          ? transformedValidation.deduplicatedNodes
          : transformedNodes

        if (transformedValidation.hasDuplicates) {
          antdNotificationRef.current.warning({
            title: 'Duplicate Node IDs Detected',
            description:
              'Some nodes had duplicate IDs and were renamed to ensure uniqueness. Please review your mindmap.',
          })
        }

        setNodes(nodesToSet)
        setEdges(transformedEdges)

        // Set the initial saved state hash
        savedStateHashRef.current = computeStateHash(nodesToSet, transformedEdges)
      } catch (error) {
        if (!isCancelled) {
          Sentry.captureException(error, {
            tags: { feature: 'mindmap', action: 'load_mindmap' },
          })
          setLoadError('An unexpected error occurred while loading the project')
        }
      } finally {
        if (!isCancelled) setIsLoading(false)
      }
    }

    void loadMindmapData()
    return () => {
      isCancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mindmapId, isPublicAccess])

  // Save function
  const save = useCallback(async () => {
    if (!mindmapId) {
      return
    }

    try {
      const backendNodes = nodes.map(toBackendNodeFormat)
      const backendEdges = edges.map((edge) =>
        toBackendEdgeFormat({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle ?? null,
          targetHandle: edge.targetHandle ?? null,
          type: edge.type,
          animated: edge.animated,
        })
      )

      const result = await MindmapService.save(mindmapId, {
        nodes: backendNodes,
        edges: backendEdges,
      })

      if (result.error?.code === 'CONTENT_SHRINK_WARNING') {
        // Backend blocked a save-to-empty — likely a stale save from an error state.
        // Mark as "saved" to stop the auto-save retry loop. The backend still holds the
        // real data; refreshing will restore it. If the user adds nodes later,
        // hasUnsavedChanges flips back to true and normal saves resume.
        console.warn('[MindMapLoader] Save-to-empty blocked by backend:', result.error.message)
        savedStateHashRef.current = currentStateHash
        return
      }

      if (result.error) {
        throw new Error(result.error.message)
      }

      // Mark current state as saved
      savedStateHashRef.current = currentStateHash
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'mindmap', action: 'save_mindmap' },
      })
      antdNotification.error({
        title: 'Auto-save failed',
        description: 'Your changes could not be saved automatically',
      })
    }
  }, [nodes, edges, mindmapId, antdNotification, currentStateHash])

  // Auto-save using SaveQueueContext
  // Disable auto-save in read-only/public view mode
  useEffect(() => {
    if (hasUnsavedChanges && !isPublicAccess) {
      saveQueue.enqueueSave(save, 3000)
    }
  }, [hasUnsavedChanges, save, saveQueue, isPublicAccess])

  // Return raw nodes/edges directly - stable refs were causing update issues
  // TODO: If zoom/pan performance becomes an issue, revisit stable refs approach
  return {
    nodes,
    edges,
    setNodes,
    setEdges,
    onNodesChange,
    onEdgesChange,
    isLoading,
    notFound,
    loadError,
    hasUnsavedChanges,
    currentStateHash,
    markAsSaved,
    isArchived,
    mindmapTitle,
    mindmapImageStyle,
    setMindmapTitle,
    setMindmapImageStyle,
  }
}
